<template>
  <div v-if="editionMode">
    <SelectAjax
      v-model:value="localCityId"
      v-model:value-object="localCityObject"
      :ajax-extra-params="ajaxExtraParams"
      :initial-params="coordinates"
      :search-debounce="instantSearchEnabled ? 300 : 1000"
      url="city-for-select"
      attribute-label="full_name"
      :label="label ?? $t('attributes.city')"
      :errors="errors"
      :name="name"
      :required="required"
      :allow-tags="allowTags"
      :highlight-if-errors="highlightIfErrors"
      @clear="clear"
      @open="loadCoordinates"
      @focus="loadCoordinatesIfGranted"
    >
      <template #option="{ option, selected, active }">
        <span
          class="flex flex-row items-center truncate"
          :class="{ 'font-medium': selected, 'font-normal': !selected }"
        >
          <span class="flex-1">{{ option.full_name }}</span>
          <span class="shrink-0">
            <img
              class="h-4 w-6 rounded"
              :src="CountryFlags[option.country.toUpperCase()]"
              :alt="option.country"
            />
          </span>
        </span>
      </template>
    </SelectAjax>
  </div>
  <div v-else>
    <BaseShowLabel
      :label="label ?? $t('attributes.city')"
      :model-value="localCityObject?.full_name"
    />
  </div>
</template>

<script>
import { defineComponent } from 'vue'
import { mapGetters } from 'vuex'
import SelectAjax from '@c/SelectAjaxComponent/component/SelectAjax.vue'
import BaseShowLabel from '@c/addf-package/components/BaseLabel/BaseShowLabel.vue'
import CountryFlags from '@/assets/enums/config/CountryFlags'
import { getCurrentCoordinates, isGeolocationGranted } from '@u/geolocation'

export default defineComponent({
  name: 'SelectCity',
  components: { BaseShowLabel, SelectAjax },
  props: {
    cityId: {
      type: [String, Number],
      required: false,
      default: null
    },
    cityObject: {
      type: Object,
      required: false,
      default: () => {}
    },
    city: {
      type: String,
      required: false,
      default: ''
    },
    zip: {
      type: String,
      required: false,
      default: ''
    },
    parentCityId: {
      type: [String, Number],
      required: false,
      default: null
    },
    country: {
      type: String,
      required: false,
      default: null
    },
    required: {
      type: Boolean,
      required: false,
      default: false
    },
    label: {
      type: String,
      required: false,
      default: null
    },
    name: {
      type: String,
      required: false,
      default: null
    },
    errors: {
      type: Object,
      required: false,
      default: () => {}
    },
    editionMode: {
      type: Boolean,
      required: false,
      default: false
    },
    allowTags: {
      type: Boolean,
      required: false,
      default: false
    },
    highlightIfErrors: {
      type: Boolean,
      required: false,
      default: false
    }
  },
  emits: [
    'update:cityId',
    'update:cityObject',
    'update:city',
    'update:zip',
    'change'
  ],
  data() {
    return {
      coordinates: null
    }
  },
  computed: {
    // city-for-select searches the Typesense index when Scout is enabled.
    ...mapGetters({
      instantSearchEnabled: 'metadata/getInstantSearchSetting'
    }),
    CountryFlags() {
      return CountryFlags
    },
    localCityId: {
      get() {
        return this.cityId
      },
      set(value) {
        this.$emit('update:cityId', value)
      }
    },
    localCityObject: {
      get() {
        return this.cityObject
      },
      set(value) {
        if (value) {
          const newCity = value?.city ?? value?.full_name ?? null
          if (newCity !== this.city) {
            this.localCity = newCity
          }
          const newZip = value?.zip ?? null
          if (newZip !== this.zip) {
            this.localZip = newZip
          }
        }
        this.$emit('update:cityObject', value)
      }
    },
    localCity: {
      get() {
        return this.city
      },
      set(value) {
        this.$emit('update:city', value)
        this.$emit('change', value)
      }
    },
    localZip: {
      get() {
        return this.zip
      },
      set(value) {
        this.$emit('update:zip', value)
      }
    },
    ajaxExtraParams() {
      const params = {}

      if (this.country !== null) {
        params.country = this.country
      }

      if (this.parentCityId !== null) {
        params['parent-city-id'] = this.parentCityId
      }

      // Equally relevant results come back nearest first.
      if (this.coordinates) {
        Object.assign(params, this.coordinates)
      }

      return Object.keys(params).length > 0 ? params : null
    }
  },
  watch: {
    city() {
      this.syncCityObject()
    },
    cityObject(newVal) {
      if (!newVal?.full_name) {
        this.syncCityObject()
      }
    }
  },
  mounted() {
    this.syncCityObject()
  },
  methods: {
    syncCityObject() {
      const missingCityObject = !this.cityObject?.full_name

      if (this.cityId && (!this.cityObject?.id || missingCityObject)) {
        this.localCityObject = {
          id: this.cityId,
          full_name: `${this.zip} ${this.city}`,
          city: this.city,
          zip: this.zip
        }
      } else if (!this.cityId && this.city && missingCityObject) {
        this.localCityObject = {
          id: null,
          full_name: this.city,
          city: this.city,
          zip: this.zip ?? null
        }
      }
    },
    // Asked when the list is opened, not on mount, so that this field never
    // prompts on its own. (InstantSearchPalette, mounted when instant search
    // is on, may already have asked: the position is shared and cached.)
    loadCoordinates() {
      getCurrentCoordinates().then((coordinates) => {
        this.coordinates = coordinates
      })
    },
    // On focus, only when no prompt would show: a typed search is then
    // sorted nearest first without the chevron being opened.
    loadCoordinatesIfGranted() {
      if (this.coordinates) {
        return
      }
      isGeolocationGranted().then((granted) => {
        if (granted) {
          this.loadCoordinates()
        }
      })
    },
    clear() {
      this.localZip = ''
      this.localCity = ''
    }
  }
})
</script>
